import { Status } from "../types/status";

// Unified status configuration utility.
//
// Loan lifecycle statuses (mirrors backend `LoanStatus` enum) advance through
// four phases - request → contract verification → disbursement-account → disburse.
// Each phase has a success and (where applicable) a failure key so the
// contract-detail page can colour the row distinctly and only show the right
// "Retry" affordance:
//
//   PENDING                       -> yellow  (waiting for operator)
//   REJECTED                      -> red
//   CONTRACT_VERIFICATION_PENDING -> blue    (loan request accepted at bank; awaiting customer contract OTP verification)
//   APPROVED                      -> green   (customer has verified the contract OTP; or legacy pre-OTP-flow row)
//   LOAN_REQUEST_FAILED           -> red     (request rejected by bank → retry available)
//   DISBURSEMENT_ACCOUNT_ADDED    -> blue    (account added at bank, ready to disburse)
//   DISBURSEMENT_ACCOUNT_FAILED   -> red     (add-account failed → retry available)
//   DISBURSED                     -> green   (terminal success)
//   DISBURSEMENT_FAILED           -> red     (disburse failed → retry available)
export const getStatusConfig = (status: string) => {
  const statusConfigMap = {
    PENDING: {
      className: "bg-yellow-50 text-yellow-800 border-yellow-200",
      dotColor: "#d97706",
      colorName: "yellow",
    },
    CONTRACT_VERIFICATION_PENDING: {
      className: "bg-blue-50 text-blue-800 border-blue-200",
      dotColor: "#1d4ed8",
      colorName: "blue",
    },
    APPROVED: {
      className: "bg-green-50 text-green-800 border-green-200",
      dotColor: "#059669",
      colorName: "green",
    },
    REJECTED: {
      className: "bg-red-50 text-red-800 border-red-200",
      dotColor: "#dc2626",
      colorName: "red",
    },
    UNDER_REVIEW: {
      className: "bg-blue-50 text-blue-800 border-blue-200",
      dotColor: "#1d4ed8",
      colorName: "blue",
    },
    LOAN_REQUEST_FAILED: {
      className: "bg-red-50 text-red-800 border-red-200",
      dotColor: "#dc2626",
      colorName: "red",
    },
    DISBURSEMENT_ACCOUNT_ADDED: {
      className: "bg-indigo-50 text-indigo-800 border-indigo-200",
      dotColor: "#4f46e5",
      colorName: "indigo",
    },
    DISBURSEMENT_ACCOUNT_FAILED: {
      className: "bg-red-50 text-red-800 border-red-200",
      dotColor: "#dc2626",
      colorName: "red",
    },
    DISBURSED: {
      className: "bg-green-50 text-green-800 border-green-200",
      dotColor: "#059669",
      colorName: "green",
    },
    DISBURSEMENT_FAILED: {
      className: "bg-red-50 text-red-800 border-red-200",
      dotColor: "#dc2626",
      colorName: "red",
    },
    COMPLETED: {
      className: "bg-gray-50 text-gray-800 border-gray-200",
      dotColor: "#4b5563",
      colorName: "gray",
    },
    DEFAULT: {
      className: "bg-gray-50 text-gray-800 border-gray-200",
      dotColor: "#4b5563",
      colorName: "gray",
    },
  };

  const statusKey = status.toUpperCase() as keyof typeof statusConfigMap;
  return statusConfigMap[statusKey] || statusConfigMap.DEFAULT;
};

/** Chip text + colours for contract entitlement loan column (dagri `LoanStatus`). */
export type EntitlementLoanStatusPresentation = {
  className: string;
  dotColor: string;
  /** Text shown on the chip (may differ from API for clarity). */
  label: string;
};

const titleCaseWords = (raw: string) =>
  raw
    .toLowerCase()
    .split("_")
    .map((word, i) =>
      i === 0 ? word.charAt(0).toUpperCase() + word.slice(1) : word,
    )
    .join(" ");

/**
 * Maps a per-entitlement loan status (backend
 * `com.kft.dagri.input.controller.LoanStatus.name()`) to chip styling + label.
 *
 * Two-step contract approval:
 *   - {@code CONTRACT_VERIFICATION_PENDING} = loan request succeeded at the bank;
 *     awaiting customer contract OTP verification (blue chip).
 *   - {@code APPROVED} = customer has verified the contract OTP, OR a legacy
 *     pre-OTP-flow row that was never backfilled. Either way the loan is
 *     bank-approved (green chip).
 *
 * The historical wire value {@code LOAN_REQUEST_APPROVED} is no longer emitted
 * by the backend (the database string was normalized to {@code APPROVED} by
 * Liquibase changeset {@code multibank-010}), but we still tolerate it
 * defensively and treat it as bank-approved.
 */
export const getEntitlementLoanStatusPresentation = (
  status: string,
): EntitlementLoanStatusPresentation => {
  const key = (status || "").toUpperCase();
  const normalized = key === "LOAN_REQUEST_APPROVED" ? "APPROVED" : key;

  const palette: Record<
    string,
    Pick<EntitlementLoanStatusPresentation, "className" | "dotColor">
  > = {
    PENDING: {
      className: "bg-yellow-50 text-yellow-800 border-yellow-200",
      dotColor: "#d97706",
    },
    REJECTED: {
      className: "bg-red-50 text-red-800 border-red-200",
      dotColor: "#dc2626",
    },
    CONTRACT_VERIFICATION_PENDING: {
      className: "bg-blue-50 text-blue-800 border-blue-200",
      dotColor: "#1d4ed8",
    },
    APPROVED: {
      className: "bg-green-50 text-green-800 border-green-200",
      dotColor: "#059669",
    },
    LOAN_REQUEST_FAILED: {
      className: "bg-red-50 text-red-800 border-red-200",
      dotColor: "#dc2626",
    },
    DISBURSEMENT_ACCOUNT_ADDED: {
      className: "bg-indigo-50 text-indigo-800 border-indigo-200",
      dotColor: "#4f46e5",
    },
    DISBURSEMENT_ACCOUNT_FAILED: {
      className: "bg-red-50 text-red-800 border-red-200",
      dotColor: "#dc2626",
    },
    DISBURSED: {
      className: "bg-green-50 text-green-800 border-green-200",
      dotColor: "#059669",
    },
    DISBURSEMENT_FAILED: {
      className: "bg-red-50 text-red-800 border-red-200",
      dotColor: "#dc2626",
    },
  };

  const defaults = {
    className: "bg-gray-50 text-gray-800 border-gray-200",
    dotColor: "#4b5563",
  };

  const styles = palette[normalized] ?? defaults;

  const labelByStatus: Record<string, string> = {
    CONTRACT_VERIFICATION_PENDING: "Awaiting Contract Verification",
    APPROVED: "Approved",
    PENDING: "Pending",
    REJECTED: "Rejected",
    LOAN_REQUEST_FAILED: "Loan Request Failed",
    DISBURSEMENT_ACCOUNT_ADDED: "Disbursement Account Added",
    DISBURSEMENT_ACCOUNT_FAILED: "Disbursement Account Failed",
    DISBURSED: "Disbursed",
    DISBURSEMENT_FAILED: "Disbursement Failed",
  };
  const label = labelByStatus[normalized] ?? titleCaseWords(status || "");

  return { ...styles, label };
};

// Voucher-specific status configuration utility
export const getStatusText = (_status: number) => {
  let status = "ISSUED";
  switch (_status) {
    case 1:
      status = "VOID";
      break;
    case 2:
      status = "ISSUED";
      break;
    case 3:
      status = "REDEEMED";
      break;
    case 4:
      status = "PARTIALLY REDEEMED";
      break;
  }
  return status;
};

export const getVoucherStatusConfig = (statusCode: number) => {
  const statusText = getStatusText(statusCode);

  const voucherStatusConfig = {
    VOID: {
      className: "bg-gray-100 text-gray-700 border-gray-300",
      dotColor: "#6b7280",
      colorName: "gray",
      icon: "❌", // Optional: can be replaced with Lucide icons
    },
    ISSUED: {
      className: "bg-blue-100 text-blue-800 border-blue-300",
      dotColor: "#1d4ed8",
      colorName: "blue",
      icon: "📄", // Document icon
    },
    REDEEMED: {
      className: "bg-green-100 text-green-800 border-green-300",
      dotColor: "#059669",
      colorName: "green",
      icon: "✅", // Checkmark
    },
    PARTIALLY_REDEEMED: {
      className: "bg-yellow-100 text-yellow-800 border-yellow-300",
      dotColor: "#d97706",
      colorName: "yellow",
      icon: "🔄", // Refresh/partial
    },
    DEFAULT: {
      className: "bg-gray-100 text-gray-700 border-gray-300",
      dotColor: "#6b7280",
      colorName: "gray",
      icon: "❓",
    },
  };

  // Normalize the status text for the key (remove spaces for object key)
  const statusKey = statusText
    .replace(/\s+/g, "_")
    .toUpperCase() as keyof typeof voucherStatusConfig;

  return voucherStatusConfig[statusKey] || voucherStatusConfig.DEFAULT;
};

// Helper function to map any status to our standard ones
export const getStatusColor = (
  status: string,
  type: "text" | "bg" = "text"
): string => {
  const statusLower = status?.toLowerCase();

  switch (statusLower) {
    case "accepted":
      return type === "text" ? "#065F46" : "#D1FAE5"; // Green
    case "pending":
      return type === "text" ? "#92400E" : "#FEF3C7"; // 🟠 Amber
    case "rejected":
    case "inactive":
      return type === "text" ? "#991B1B" : "#FECACA"; // 🔴 Red
    case "active":
      return type === "text" ? "#166534" : "#BBF7D0"; // 🟢 Green tone
    default:
      return type === "text" ? "#6B7280" : "#F3F4F6"; // fallback (neutral gray)
  }
};

export const mapStatusToStandard = (status: string): Status => {
  const statusMap: { [key: string]: Status } = {
    accepted: "accepted",
    pending: "pending",
    rejected: "rejected",
    approved: "accepted",
    declined: "rejected",
    cancelled: "rejected",
  };
  return statusMap[status] || "pending";
};
